// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import { h } from 'vue';
import { RouterView } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// ── Views (light ones can stay eager; heavier ones lazy) ─────────────────
import LoginPage from '@/views/LoginPage.vue';
import PaymentSuccess from '@/views/PaymentSuccess.vue';
import AskAIPage from '@/views/AskAIPage.vue';
import ContextPage from '@/views/ContextPage.vue';
import LoadingPage from '@/views/LoadingPage.vue';
import ResponsePage from '@/views/ResponsePage.vue';
import ForgotPassword from '@/views/ForgotPassword.vue';
import ResetPassword from '@/views/ResetPassword.vue';

import AdminDashboard from '@/views/AdminDashboard.vue';
const AdminUsageLog = () => import('@/views/AdminUsageLog.vue');
const AdminUsers = () => import('@/views/AdminUsers.vue');
const AdminRoles = () => import('@/views/AdminRoles.vue');
const AdminPayments = () => import('@/views/AdminPayments.vue');

import MarketplaceView from '@/views/MarketplaceView.vue';
import ToolDetailView from '@/views/ToolDetailView.vue';
import FinancialRatioCalculatorView from '@/views/FinancialRatioCalculatorView.vue';
import GSTInvoiceView from '@/views/GSTInvoiceView.vue';

// Vite uses import.meta.env; Vue CLI uses process.env. Keep process.env with default.
const HIDE_GST = String(process.env.VUE_APP_HIDE_GST_INVOICE || 'false').toLowerCase() === 'true';

// ── Helpers ──────────────────────────────────────────────────────────────
function safeParseJwt(token) {
  try {
    const [, payloadB64] = token.split('.');
    const json = atob((payloadB64 || '').replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json || '{}');
  } catch {
    return {};
  }
}

function hasValidLocalJwt() {
  const token = localStorage.getItem('auth_token');
  if (!token) return false;
  const p = safeParseJwt(token);
  return typeof p.exp === 'number' && p.exp * 1000 > Date.now();
}

function getRoleFromLocalJwt() {
  const token = localStorage.getItem('auth_token');
  if (!token) return undefined;
  const p = safeParseJwt(token);
  return p.role || p.role_id; // support either field name
}

function effectiveRole(auth) {
  // Prefer hydrated cookie-mode user role, fallback to URL-token mode
  return auth?.user?.role || auth?.user?.role_id || getRoleFromLocalJwt();
}

const routes = [
  // ─── Public ───────────────────────────────────────────────────────────
  { path: '/', name: 'Home', component: LoginPage },
  { path: '/login', name: 'Login', component: LoginPage },
  { path: '/payment-success', name: 'PaymentSuccess', component: PaymentSuccess },
  { path: '/forgot-password', name: 'ForgotPassword', component: ForgotPassword },
  { path: '/reset-password', name: 'ResetPassword', component: ResetPassword },

  // Google OAuth URL-token callback (public)
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/AuthCallback.vue'),
    meta: { isAuthCallback: true },
  },

  // ─── Authenticated section under AdminDashboard ───────────────────────
  {
    path: '/',
    component: AdminDashboard,
    meta: { requiresAuth: true },
    children: [
      // Ask-AI flow
      { path: 'ask-ai', name: 'AskAI', component: AskAIPage },
      { path: 'context', name: 'Context', component: ContextPage },
      { path: 'loading', name: 'Loading', component: LoadingPage },
      { path: 'response', name: 'Response', component: ResponsePage },

      // Marketplace
      { path: 'marketplace', name: 'Marketplace', component: MarketplaceView },

      // Tools (explicit, then dynamic)
      {
        path: 'tool/financial-ratio-calculator',
        name: 'FinancialRatioCalculator',
        component: FinancialRatioCalculatorView,
      },
      {
        path: 'tool/gst-invoice',
        name: 'GSTInvoice',
        component: GSTInvoiceView,
      },

      // Cash-Flow Simulator
      {
        path: 'tool/cashflow-simulator',
        component: { render: () => h(RouterView) },
        children: [
          { path: '', redirect: { name: 'cf-upload' } },
          {
            path: 'upload',
            name: 'cf-upload',
            component: () => import('@/modules/cashflow-simulator/pages/UploadPage.vue'),
          },
          {
            path: 'scenario',
            name: 'cf-scenario',
            component: () => import('@/modules/cashflow-simulator/pages/ScenarioPage.vue'),
          },
          // Prefer results with a runId param
          {
            path: 'results/:runId',
            name: 'cf-results',
            component: () => import('@/modules/cashflow-simulator/pages/ResultsPage.vue'),
            props: true, // optional; lets you receive runId as a prop if you want
          },
          // Back-compat: if someone hits /results without a param, redirect to Upload or a list page.
          // (You can also redirect to latest run if you implement that lookup in beforeEnter.)
          {
            path: 'results',
            redirect: { name: 'cf-upload' },
          },

        ],
      },

      // Generic dynamic tool (keep after explicit tool routes)
      {
        path: 'tool/:id',
        name: 'ToolDetail',
        component: ToolDetailView,
        props: true,
      },

      // Usage Logs
      { path: 'usage-logs', name: 'AdminUsageLog', component: AdminUsageLog },

      // Admin-only section
      {
        path: 'admin-dashboard',
        children: [
          { path: '', name: 'AdminDashboard', redirect: 'users' },
          { path: 'users', name: 'AdminUsers', component: AdminUsers },
          { path: 'roles', name: 'AdminRoles', component: AdminRoles },
          { path: 'payments', name: 'AdminPayments', component: AdminPayments },
        ],
      },

      {
        path: '/auth/callback',
        name: 'AuthCallback',
        component: () => import('@/views/AuthCallback.vue'),
      }

    ],
  },

  // ─── Catch-all ────────────────────────────────────────────────────────
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Optional: keep UX smooth when moving between long pages
  scrollBehavior() {
    return { top: 0 };
  },
});

// Hydrate only once per page load to support cookie-based sessions
let didHydrateOnce = false;

// ─── Global navigation guard ────────────────────────────────────────────
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore();

  // Skip hydrate on OAuth callback; that component handles consumption
  const isCallback = !!to.meta?.isAuthCallback;

  // 0) One-time hydrate (cookie session support; no local token needed)
  if (!didHydrateOnce && !isCallback) {
    didHydrateOnce = true;
    try {
      await auth.hydrate(); // sets auth.user when httpOnly cookie session exists
    } catch {
      // ignore
    }
  }

  const isAuth = auth.loggedIn || hasValidLocalJwt();

  // 1) “/” → dashboard if already logged in
  if (to.path === '/' && isAuth) {
    const role = effectiveRole(auth);
    return next(['admin', 'super_admin'].includes(role) ? { name: 'AdminDashboard' } : { name: 'AskAI' });
  }

  // 2) Allow public pages
  const publicNames = new Set(['Home', 'Login', 'PaymentSuccess', 'ForgotPassword', 'ResetPassword', 'AuthCallback']);
  if (to.name && publicNames.has(to.name)) {
    return next();
  }

  // 3) Everything else requires auth
  if (!isAuth) {
    return next({ name: 'Login', query: { redirect: to.fullPath } });
  }

  // 4) Guard admin-only routes
  if (to.path.startsWith('/admin-dashboard')) {
    const role = effectiveRole(auth);
    if (!['admin', 'super_admin'].includes(role)) {
      return next({ name: 'AskAI' });
    }
  }

  // 5) Kill switch for GST invoice route
  if (HIDE_GST && (to.name === 'GSTInvoice' || to.path === '/tool/gst-invoice')) {
    return next({ name: 'Marketplace' });
  }

  // 6) OK to proceed
  return next();
});

export default router;
